import React from 'react';
import { getProgramCardByCode } from '../../services/rpc/programs';
import { normalizeProgramCardPayload } from '../../utils/programCardNormalizer';
import type { ProgramCardPayload } from '../../types/program-card';
import styles from './ProgramCardV2.module.css';

// Utility: parse hash query parameters
function getHashQuery(): Record<string, string> {
  const hash = window.location.hash || '';
  const query = hash.split('?')[1] || '';
  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};
  params.forEach((v, k) => { result[k] = v; });
  return result;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as Record<string, unknown>).message === 'string') {
    return (error as { message: string }).message;
  }
  return fallback;
};

// NEW: Calculate deadline urgency
function calculateDeadlineUrgency(deadline: string | undefined | null): {
  daysRemaining: number | null;
  urgencyLevel: 'urgent' | 'moderate' | 'comfortable' | 'unknown';
  colorClass: string;
} {
  if (!deadline || deadline === '—' || deadline === 'Not specified') {
    return { daysRemaining: null, urgencyLevel: 'unknown', colorClass: 'deadlineUnknown' };
  }

  try {
    const deadlineDate = new Date(deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadlineDate.setHours(0, 0, 0, 0);

    const timeDiff = deadlineDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (daysRemaining < 0) {
      return { daysRemaining, urgencyLevel: 'unknown', colorClass: 'deadlinePassed' };
    } else if (daysRemaining <= 7) {
      return { daysRemaining, urgencyLevel: 'urgent', colorClass: 'deadlineUrgent' };
    } else if (daysRemaining <= 30) {
      return { daysRemaining, urgencyLevel: 'moderate', colorClass: 'deadlineModerate' };
    } else {
      return { daysRemaining, urgencyLevel: 'comfortable', colorClass: 'deadlineComfortable' };
    }
  } catch {
    return { daysRemaining: null, urgencyLevel: 'unknown', colorClass: 'deadlineUnknown' };
  }
}

// NEW: Generate eligibility summary
function generateEligibilitySummary(data: ProgramCardPayload | null): string[] {
  const summary: string[] = [];
  if (!data) return summary;

  const elig = data.eligibility || {};

  if (elig.isbn_required === true) {
    summary.push('ISBN Required');
  } else if (elig.isbn_required === false) {
    summary.push('No ISBN Needed');
  }

  if (elig.independent_publishers_allowed === true) {
    summary.push('Self-Published Welcome');
  } else if (elig.independent_publishers_allowed === false) {
    summary.push('Traditional Only');
  }

  if (Array.isArray(elig.geographic_restrictions) && elig.geographic_restrictions.length > 0) {
    summary.push(`Region: ${elig.geographic_restrictions[0]}`);
  }

  if (Array.isArray(elig.accepted_formats) && elig.accepted_formats.length > 0) {
    summary.push(elig.accepted_formats.slice(0, 2).join(', '));
  }

  return summary;
}

// Data completeness calculation
function calculateCompleteness(data: ProgramCardPayload | null): {
  overall: number;
  sections: Record<string, { percentage: number; filled: number; total: number }>;
  interpretation: string;
} {
  if (!data) return { overall: 0, sections: {}, interpretation: 'No data available' };

  const sections: Record<string, { filled: number; total: number }> = {
    requirements: { filled: 0, total: 6 },
    deadlines: { filled: 0, total: 5 },
    fees_prizes: { filled: 0, total: 5 },
    contacts: { filled: 0, total: 4 },
    judging: { filled: 0, total: 2 },
    media: { filled: 0, total: 3 },
  };

  // Requirements
  const elig = data.eligibility || {};
  if (elig.isbn_required != null) sections.requirements.filled++;
  if (elig.independent_publishers_allowed != null) sections.requirements.filled++;
  if (elig.geographic_restrictions && Array.isArray(elig.geographic_restrictions) && elig.geographic_restrictions.length > 0) sections.requirements.filled++;
  if (elig.requirements_summary && elig.requirements_summary !== '—') sections.requirements.filled++;
  if (elig.accepted_formats && Array.isArray(elig.accepted_formats) && elig.accepted_formats.length > 0) sections.requirements.filled++;
  if (elig.deadline_type && elig.deadline_type !== '—') sections.requirements.filled++;

  // Deadlines
  const time = data.timeline || {};
  if (time.submission_period && time.submission_period !== '—') sections.deadlines.filled++;
  if (time.regular_deadline && time.regular_deadline !== '—') sections.deadlines.filled++;
  if (time.final_deadline && time.final_deadline !== '—') sections.deadlines.filled++;
  if (time.winners_announcement_date && time.winners_announcement_date !== '—') sections.deadlines.filled++;
  if (time.deadline_structure && time.deadline_structure !== '—') sections.deadlines.filled++;

  // Fees & Prizes
  const fees = data.fees_prizes || {};
  if (fees.entry_fee_min != null || fees.entry_fee_max != null) sections.fees_prizes.filled++;
  if (fees.prize_structure && fees.prize_structure !== '—') sections.fees_prizes.filled++;
  if (fees.prize_types && Array.isArray(fees.prize_types) && fees.prize_types.length > 0) sections.fees_prizes.filled++;
  if (fees.total_win_opportunities != null) sections.fees_prizes.filled++;
  if (fees.extra_benefits && Array.isArray(fees.extra_benefits) && fees.extra_benefits.length > 0) sections.fees_prizes.filled++;

  // Contacts
  const contacts = data.contacts || {};
  if (contacts.contact_name && contacts.contact_name !== '—') sections.contacts.filled++;
  if (contacts.contact_email && contacts.contact_email !== '—') sections.contacts.filled++;
  if (contacts.contact_phone && contacts.contact_phone !== '—') sections.contacts.filled++;
  if (contacts.physical_address && contacts.physical_address !== '—') sections.contacts.filled++;

  // Judging
  const judging = data.judging || {};
  if (judging.judging_criteria && judging.judging_criteria !== '—') sections.judging.filled++;
  if (judging.celebration_type && judging.celebration_type !== '—') sections.judging.filled++;

  // Media
  const urls = data.urls || {};
  if (urls.website_url && urls.website_url !== '—') sections.media.filled++;
  if (urls.submission_url && urls.submission_url !== '—') sections.media.filled++;
  if (urls.past_winners_url && urls.past_winners_url !== '—') sections.media.filled++;

  // Calculate percentages
  const sectionsWithPercentage = Object.entries(sections).reduce((acc, [key, { filled, total }]) => {
    acc[key] = { filled, total, percentage: Math.round((filled / total) * 100) };
    return acc;
  }, {} as Record<string, { percentage: number; filled: number; total: number }>);

  // Weighted overall score (Requirements 25%, Deadlines 20%, Fees & Prizes 20%, Contacts 15%, Judging 10%, Media 10%)
  const weights = { requirements: 0.25, deadlines: 0.20, fees_prizes: 0.20, contacts: 0.15, judging: 0.10, media: 0.10 };
  const overall = Math.round(
    Object.entries(sectionsWithPercentage).reduce((sum, [key, { percentage }]) => {
      return sum + (percentage * (weights[key as keyof typeof weights] || 0));
    }, 0)
  );

  // Generate interpretation
  let interpretation = '';
  const deadlinesPercent = sectionsWithPercentage.deadlines.percentage;
  const feesPercent = sectionsWithPercentage.fees_prizes.percentage;
  const contactsPercent = sectionsWithPercentage.contacts.percentage;

  if (overall >= 80) {
    interpretation = 'Excellent data coverage';
  } else if (overall >= 60) {
    interpretation = 'Good data coverage';
  } else if (overall >= 40) {
    interpretation = 'Moderate data coverage';
  } else {
    interpretation = 'Limited data coverage';
  }

  const details: string[] = [];
  if (deadlinesPercent >= 80) details.push('strong deadlines data');
  if (feesPercent >= 80) details.push('strong fees & prizes data');
  if (feesPercent < 40) details.push('prizes incomplete');
  if (contactsPercent < 40) details.push('contacts incomplete');

  if (details.length > 0) {
    interpretation += ` (${details.join(', ')})`;
  }

  return { overall, sections: sectionsWithPercentage, interpretation };
}

const ProgramCardV2: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<ProgramCardPayload | null>(null);
  const [expandedFields, setExpandedFields] = React.useState<Set<string>>(new Set());

  const programCode = React.useMemo(() => {
    const qp = getHashQuery();
    return qp['program_code'] || '';
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      if (!programCode) {
        setError('Missing program_code in URL');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const rpcData = await getProgramCardByCode(programCode);
        const normalized = normalizeProgramCardPayload(rpcData ?? {});
        if (mounted) setData(normalized);
      } catch (e: unknown) {
        if (mounted) setError(getErrorMessage(e, 'Failed to load program'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [programCode]);

  const completeness = React.useMemo(() => calculateCompleteness(data), [data]);
  const eligibilitySummary = React.useMemo(() => generateEligibilitySummary(data), [data]);

  const toggleFieldExpansion = (fieldKey: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  };

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Loading program card…</div></div>;
  }

  if (error) {
    return <div className={styles.container}><div className={styles.error}>{error}</div></div>;
  }

  const prog = data?.program;
  const rv = data?.ratings_vetting;
  const urls = data?.urls;
  const contacts = data?.contacts;
  const elig = data?.eligibility || {};
  const time = data?.timeline || {};
  const fees = data?.fees_prizes || {};
  const judging = data?.judging || {};

  // Calculate deadline urgency
  const nextDeadline = time.early_bird_deadline || time.regular_deadline || time.final_deadline;
  const urgency = calculateDeadlineUrgency(nextDeadline);

  // Helper formatters
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return 'Not specified';
    if (Array.isArray(v)) return v.length ? v.join(', ') : 'Not specified';
    return String(v);
  };

  const fmtBool = (v: unknown): React.ReactNode => {
    if (v == null) return <span className={styles.badgeNeutral}>Not specified</span>;
    return v ? <span className={styles.badgeYes}>Yes</span> : <span className={styles.badgeNo}>No</span>;
  };

  const fmtCurrency = (min: unknown, max: unknown, currency?: string | null): string => {
    try {
      if (min == null && max == null) return 'Not specified';
      const cur = currency || 'USD';
      if (min != null && max != null && min === max) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(min)) + ` ${cur}`;
      }
      const minStr = min != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(min)) : '';
      const maxStr = max != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(max)) : '';
      if (minStr && maxStr) return `${minStr} - ${maxStr} ${cur}`;
      return (minStr || maxStr || 'Not specified') + (minStr || maxStr ? ` ${cur}` : '');
    } catch {
      return 'Not specified';
    }
  };

  const getVettingBadgeClass = (status: string | undefined) => {
    if (!status) return styles.vettingNeutral;
    const lower = status.toLowerCase();
    if (lower === 'green') return styles.vettingGreen;
    if (lower === 'yellow') return styles.vettingYellow;
    if (lower === 'red') return styles.vettingRed;
    return styles.vettingNeutral;
  };

  // Helper for long text truncation
  const renderExpandableText = (text: string | null | undefined, fieldKey: string, maxLength = 200): React.ReactNode => {
    if (!text || text === '—' || text === 'Not specified') return 'Not specified';

    const isExpanded = expandedFields.has(fieldKey);
    const needsTruncation = text.length > maxLength;

    if (!needsTruncation) return text;

    return (
      <>
        {isExpanded ? text : `${text.substring(0, maxLength)}...`}
        <button
          className={styles.expandBtn}
          onClick={() => toggleFieldExpansion(fieldKey)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      </>
    );
  };

  return (
    <div className={styles.container}>
      {/* TIER 1: Redesigned Header with unified block styling */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.logo}>
            <img src="/BAM-logo-embedded.svg" alt="Book Award Mastery" />
          </div>
          <div className={styles.headerActions}>
            <button className={styles.printBtn} onClick={() => window.print()}>
              Print Report Card
            </button>
          </div>
        </div>

        <div className={styles.headerMain}>
          <h1 className={styles.programTitle}>{prog?.name ?? 'Unknown Program'}</h1>
          <div className={styles.headerMeta}>
            <span className={styles.organization}>{prog?.awarding_organization ?? 'Not specified'}</span>
            {prog?.foundation_year && <span className={styles.foundationYear}>Est. {prog.foundation_year}</span>}
            <span className={`${styles.vettingBadge} ${getVettingBadgeClass(rv?.vetting_status ?? undefined)}`}>
              {rv?.vetting_status?.toUpperCase() ?? 'UNKNOWN'}
            </span>
            {/* TIER 1: Display data quality score */}
            {rv?.data_quality_score != null && (
              <span className={styles.qualityScore} title="Data Quality Score">
                Quality: {rv.data_quality_score}%
              </span>
            )}
          </div>
        </div>

        {/* TIER 2: At-a-glance summary section */}
        <div className={styles.atAGlance}>
          <h2 className={styles.atAGlanceTitle}>At a Glance</h2>
          <div className={styles.atAGlanceGrid}>
            <div className={styles.glanceItem}>
              <span className={styles.glanceIcon}>📅</span>
              <div className={styles.glanceContent}>
                <span className={styles.glanceLabel}>Next Deadline</span>
                <span className={`${styles.glanceValue} ${styles[urgency.colorClass]}`}>
                  {nextDeadline || 'Not specified'}
                  {urgency.daysRemaining !== null && urgency.daysRemaining >= 0 && (
                    <span className={styles.daysRemaining}> ({urgency.daysRemaining} days)</span>
                  )}
                </span>
              </div>
            </div>
            <div className={styles.glanceItem}>
              <span className={styles.glanceIcon}>💰</span>
              <div className={styles.glanceContent}>
                <span className={styles.glanceLabel}>Entry Fee</span>
                <span className={styles.glanceValue}>
                  {fmtCurrency(fees.entry_fee_min, fees.entry_fee_max, fees.entry_fee_currency ?? fees.primary_currency ?? undefined)}
                </span>
              </div>
            </div>
            <div className={styles.glanceItem}>
              <span className={styles.glanceIcon}>🏆</span>
              <div className={styles.glanceContent}>
                <span className={styles.glanceLabel}>Win Opportunities</span>
                <span className={styles.glanceValue}>
                  {fees.total_win_opportunities ?? 'Not specified'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* TIER 1: Decision Metrics Card */}
        <div className={styles.decisionMetrics}>
          <h2 className={styles.metricsTitle}>Decision Metrics</h2>
          <div className={styles.metricsGrid}>
            {/* TIER 1: Carol's Rating */}
            {rv?.carol_s_rating != null && (
              <div className={styles.metricCard}>
                <span className={styles.metricLabel}>Carol's Rating</span>
                <span className={styles.metricValue}>
                  {'⭐'.repeat(Math.round(rv.carol_s_rating))}
                  <span className={styles.metricNumber}> {rv.carol_s_rating}/5</span>
                </span>
              </div>
            )}
            {/* Data Quality Score */}
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Data Quality</span>
              <span className={styles.metricValue}>{completeness.overall}%</span>
              <div className={styles.metricBar}>
                <div className={styles.metricBarFill} style={{ width: `${completeness.overall}%` }}></div>
              </div>
            </div>
            {/* Deadline Urgency */}
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Deadline Status</span>
              <span className={`${styles.metricValue} ${styles[urgency.colorClass]}`}>
                {urgency.daysRemaining !== null && urgency.daysRemaining >= 0
                  ? `${urgency.daysRemaining} days`
                  : urgency.daysRemaining !== null && urgency.daysRemaining < 0
                  ? 'Passed'
                  : 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* TIER 1: Key Requirements Strip */}
        <div className={styles.requirementsStrip}>
          <h3 className={styles.stripTitle}>Key Requirements</h3>
          <div className={styles.requirementsList}>
            {eligibilitySummary.length > 0 ? (
              eligibilitySummary.map((req, idx) => (
                <span key={idx} className={styles.requirementBadge}>{req}</span>
              ))
            ) : (
              <span className={styles.requirementBadge}>Requirements not specified</span>
            )}
          </div>
        </div>

        {/* Key CTAs */}
        <nav className={styles.ctaBar} aria-label="Program actions">
          {urls?.website_url && (
            <a href={urls.website_url} target="_blank" rel="noopener noreferrer" className={styles.ctaLink}>
              Official Website →
            </a>
          )}
          {urls?.submission_url && (
            <a href={urls.submission_url} target="_blank" rel="noopener noreferrer" className={`${styles.ctaLink} ${styles.ctaPrimary}`}>
              Submission Portal →
            </a>
          )}
          {urls?.past_winners_url && (
            <a href={urls.past_winners_url} target="_blank" rel="noopener noreferrer" className={styles.ctaLink}>
              Past Winners →
            </a>
          )}
        </nav>
      </header>

      <main className={styles.main}>
        {/* Requirements Section */}
        <section id="requirements" className={styles.section}>
          <h2 className={styles.sectionTitle}>Requirements</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.label}>ISBN Required</span>
              <span className={styles.value}>{fmtBool(elig.isbn_required)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Independent Publishers Allowed</span>
              <span className={styles.value}>{fmtBool(elig.independent_publishers_allowed)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Geographic Restrictions</span>
              <span className={styles.value}>{fmt(elig.geographic_restrictions)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Accepted Formats</span>
              <span className={styles.value}>{fmt(elig.accepted_formats)}</span>
            </div>
            {/* TIER 2: Expandable long text */}
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <span className={styles.label}>Requirements Summary</span>
              <span className={styles.value}>
                {renderExpandableText(elig.requirements_summary, 'requirements_summary')}
              </span>
            </div>
          </div>
        </section>

        {/* Deadlines Section */}
        <section id="deadlines" className={styles.section}>
          <h2 className={styles.sectionTitle}>Deadlines</h2>
          <div className={styles.timeline}>
            <div className={styles.timelineItem}>
              <span className={styles.timelineLabel}>Early Bird</span>
              <span className={styles.timelineDate}>{time.early_bird_deadline || 'N/A'}</span>
            </div>
            <div className={styles.timelineItem}>
              <span className={styles.timelineLabel}>Regular</span>
              <span className={styles.timelineDate}>{time.regular_deadline || 'N/A'}</span>
            </div>
            <div className={styles.timelineItem}>
              <span className={styles.timelineLabel}>Final</span>
              <span className={styles.timelineDate}>{time.final_deadline || 'N/A'}</span>
            </div>
          </div>
          <div className={styles.grid} style={{ marginTop: '1rem' }}>
            <div className={styles.field}>
              <span className={styles.label}>Winners Announcement</span>
              <span className={styles.value}>{fmt(time.winners_announcement_date)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Award Ceremony</span>
              <span className={styles.value}>{fmt(time.award_ceremony_date)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Deadline Structure</span>
              <span className={styles.value}>{fmt(time.deadline_structure)}</span>
            </div>
          </div>
        </section>

        {/* Fees & Prizes Section */}
        <section id="fees-prizes" className={styles.section}>
          <h2 className={styles.sectionTitle}>Fees & Prizes</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.label}>Entry Fee Range</span>
              <span className={styles.value}>{fmtCurrency(fees.entry_fee_min, fees.entry_fee_max, fees.entry_fee_currency ?? fees.primary_currency ?? undefined)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Total Win Opportunities</span>
              <span className={styles.value}>{fmt(fees.total_win_opportunities)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Tiered Prizes</span>
              <span className={styles.value}>{fmtBool(fees.has_tiered_prizes)}</span>
            </div>
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <span className={styles.label}>Prize Types</span>
              <span className={styles.value}>
                {Array.isArray(fees.prize_types) && fees.prize_types.length > 0
                  ? fees.prize_types.map((type, i) => <span key={i} className={styles.badge}>{type}</span>)
                  : 'Not specified'}
              </span>
            </div>
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <span className={styles.label}>Extra Benefits</span>
              <span className={styles.value}>
                {Array.isArray(fees.extra_benefits) && fees.extra_benefits.length > 0
                  ? fees.extra_benefits.map((benefit, i) => <span key={i} className={styles.badge}>{benefit}</span>)
                  : 'Not specified'}
              </span>
            </div>
          </div>
        </section>

        {/* Judging Section */}
        <section id="judging" className={styles.section}>
          <h2 className={styles.sectionTitle}>Judging</h2>
          <div className={styles.grid}>
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <span className={styles.label}>Judging Criteria</span>
              <span className={styles.value}>
                {renderExpandableText(
                  typeof judging.judging_criteria === 'string' ? judging.judging_criteria : null,
                  'judging_criteria'
                )}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Celebration Type</span>
              <span className={styles.value}>{fmt(judging.celebration_type)}</span>
            </div>
          </div>
        </section>

        {/* Contacts Section */}
        <section id="contacts" className={styles.section}>
          <h2 className={styles.sectionTitle}>Contacts</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.label}>Contact Name</span>
              <span className={styles.value}>{fmt(contacts?.contact_name)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Email</span>
              <span className={styles.value}>
                {contacts?.contact_email ? (
                  <a href={`mailto:${contacts.contact_email}`}>{contacts.contact_email}</a>
                ) : 'Not specified'}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Phone</span>
              <span className={styles.value}>
                {contacts?.contact_phone ? (
                  <a href={`tel:${contacts.contact_phone}`}>{contacts.contact_phone}</a>
                ) : 'Not specified'}
              </span>
            </div>
            {contacts?.physical_address && (
              <div className={`${styles.field} ${styles.fieldFull}`}>
                <span className={styles.label}>Address</span>
                <span className={styles.value}>{contacts.physical_address}</span>
              </div>
            )}
          </div>
        </section>

        {/* TIER 2: Enhanced Data Completeness Section */}
        <section id="data-completeness" className={styles.section}>
          <h2 className={styles.sectionTitle}>Data Completeness</h2>
          <div className={styles.completenessOverall}>
            <div className={styles.overallScore}>
              <span className={styles.scoreValue}>{completeness.overall}%</span>
              <span className={styles.scoreLabel}>Overall Score</span>
            </div>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${completeness.overall}%` }}></div>
            </div>
            {/* TIER 2: Data completeness interpretation */}
            <p className={styles.completenessInterpretation}>{completeness.interpretation}</p>
          </div>
          <div className={styles.completenessGrid}>
            {Object.entries(completeness.sections).map(([key, { percentage, filled, total }]) => (
              <div key={key} className={styles.completenessItem}>
                <div className={styles.completenessHeader}>
                  <span className={styles.completenessLabel}>{key.replace('_', ' ')}</span>
                  <span className={styles.completenessPercent}>{percentage}%</span>
                </div>
                <div className={styles.progressBarSmall}>
                  <div className={styles.progressFillSmall} style={{ width: `${percentage}%` }}></div>
                </div>
                <div className={styles.completenessDetail}>{filled}/{total} fields</div>
              </div>
            ))}
          </div>
        </section>

        {/* Categories Section */}
        {data?.categories && data.categories.length > 0 && (
          <section id="categories" className={styles.section}>
            <h2 className={styles.sectionTitle}>Categories ({data.categories.length})</h2>
            <div className={styles.categoriesList}>
              {data.categories.map((cat, idx) => (
                <div key={cat.id || idx} className={styles.categoryItem}>
                  <div className={styles.categoryName}>{cat.display_name || cat.name || 'Unnamed Category'}</div>
                  {(cat.path || cat.category_path) && (
                    <div className={styles.categoryPath}>{cat.path || cat.category_path}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <p>Generated at: {data?.generated_at || new Date().toLocaleString()}</p>
        <p>🤖 Generated with Book Award Mastery</p>
      </footer>
    </div>
  );
};

export default ProgramCardV2;
